import DateRangePicker from '@components/Shared/DateRangePicker';
import { type DateRangePreset, narrowestRangeWithData, rangeStartFor } from '@lib/charts/dateRange';
import { bucketKey, formatBucket, granularityForSpan } from '@lib/charts/timeBuckets';
import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import { Box, Typography, useTheme } from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { NoDataContainer, NoDataDescription, NoDataIcon, NoDataTitle } from './Overview.styled';
import useNoticeColors from '@store/hooks/useNoticeColors';
interface OverTimeChartProps {
  inspections: IInspection[]
}

interface ChartDataPoint {
  date: string
  ok: number
  warning: number
  error: number
  notChecked: number
}

type StatusKey = Exclude<keyof ChartDataPoint, 'date'>;

const OvertimeChart = ({ inspections }: OverTimeChartProps) => {
  const { t, currentLanguage } = useLocalization();
  const noticeColors = useNoticeColors();
  const theme = useTheme();

  // Scopes this chart only: everything else on the tab keeps counting every inspection.
  // Null means the reader has not chosen, so the range follows the data and keeps following
  // it as more loads. Once they pick one, their choice sticks.
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset | null>(null);

  const inspectionDates = useMemo(
    () => (inspections ?? []).map(i => new Date(i.info.dateModified)),
    [inspections]
  );

  const dateRangePreset = selectedPreset ?? narrowestRangeWithData(inspectionDates);

  const visibleInspections = useMemo(() => {
    if (dateRangePreset === 0) return inspections ?? [];

    const start = rangeStartFor(dateRangePreset);

    return (inspections ?? []).filter(i => new Date(i.info.dateModified) >= start);
  }, [inspections, dateRangePreset]);

  const { chartData, granularity } = useMemo(() => {
    if (visibleInspections.length === 0) {
      return { chartData: [] as ChartDataPoint[], granularity: 'day' as const };
    }

    const granularity = granularityForSpan(visibleInspections.map(i => new Date(i.info.dateModified)));
    const dateMap = new Map<string, ChartDataPoint>();

    visibleInspections.forEach((inspection) => {
      const pois = inspection.pointOfInterests ?? [];

      const date = new Date(inspection.info.dateModified);
      const dateKey = bucketKey(date, granularity);

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {
          date: dateKey,
          ok: 0,
          warning: 0,
          error: 0,
          notChecked: 0,
        });
      }

      const dataPoint = dateMap.get(dateKey)!;

      let hasError = false;
      let hasWarning = false;
      let hasNotChecked = false;
      let allOk = true;

      pois.forEach((poi) => {
        const status = SPInspection.getPOIStatus(poi);
        if (status === 'Error') {
          hasError = true;
          allOk = false;
        } else if (status === 'Warning') {
          hasWarning = true;
          allOk = false;
        } else if (status === 'NotChecked') {
          hasNotChecked = true;
          allOk = false;
        }
      });

      if (hasError) {
        dataPoint.error++;
      } else if (hasWarning) {
        dataPoint.warning++;
      } else if (hasNotChecked) {
        dataPoint.notChecked++;
      } else if (allOk && pois.length > 0) {
        dataPoint.ok++;
      } else {
        dataPoint.notChecked++;
      }
    });

    const result = Array.from(dateMap.values());
    result.sort((a, b) => a.date.localeCompare(b.date));

    return { chartData: result, granularity };
  }, [visibleInspections]);

  const hasInspections = (inspections?.length ?? 0) > 0;
  const isEmpty = chartData.length === 0;

  const series: { key: StatusKey, name: string, color: string }[] = [
    { key: 'ok', name: t('Common_Successful'), color: noticeColors.Ok },
    { key: 'warning', name: t('Common_Warning'), color: noticeColors.Warning },
    { key: 'error', name: t('Common_Error'), color: noticeColors.Error },
    { key: 'notChecked', name: t('Common_NotChecked'), color: noticeColors.NotChecked },
  ];

  // A status that never occurs would only add an empty slot to every group.
  const activeSeries = series.filter(s => chartData.some(point => point[s.key] > 0));

  // The counts are small integers, so label the bars directly while the axis has room for it.
  const showValueLabels = chartData.length <= 12;

  const formatDate = (dateStr: string) => formatBucket(dateStr, granularity, currentLanguage);

  const noData = (
    <NoDataContainer>
      <NoDataIcon />
      <NoDataTitle variant="h6">
        {t('Project_Overview_NoData')}
      </NoDataTitle>
      <NoDataDescription variant="body2">
        {t('Project_Overview_NoData_Subtitle')}
      </NoDataDescription>
    </NoDataContainer>
  );

  if (!hasInspections) return noData;

  return (
    // Fills the card and hands the leftover height to whichever of the two bodies renders,
    // so neither the chart nor the empty state can push past the card's edge.
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', m: 2, mb: 4 }}>
        {series.map(({ key, name, color }) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, mr: 1 }} />
            <Typography variant="body2">{name}</Typography>
          </Box>
        ))}
        {/* Stays put when the range comes back empty, so the reader can always widen it again. */}
        <Box sx={{ ml: 'auto' }}>
          <DateRangePicker value={dateRangePreset} onChange={setSelectedPreset} dates={inspectionDates} />
        </Box>
      </Box>
      {isEmpty ? noData : <Box sx={{ flex: 1, minHeight: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 16,
              right: 30,
              left: 20,
              bottom: 5,
            }}
            barGap={2}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tickFormatter={formatDate}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: theme.palette.action.hover }}
              labelFormatter={formatDate}
              contentStyle={{
                backgroundColor: '#030303',
                border: '1px solid #333',
                borderRadius: 8,
                color: '#F1F1F2',
                fontSize: 12,
              }}
              labelStyle={{
                color: '#FFFFFF',
                fontWeight: 'bold',
              }}
              itemStyle={{
                color: '#E1E1E2',
              }}
            />
            {activeSeries.map(({ key, name, color }) => (
              <Bar key={key} dataKey={key} name={name} fill={color} radius={[4, 4, 0, 0]} maxBarSize={32}>
                {showValueLabels && (
                  <LabelList
                    dataKey={key}
                    position="top"
                    fontSize={11}
                    fill={theme.palette.text.secondary}
                    formatter={(value: number) => (value > 0 ? value : '')}
                  />
                )}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>}
    </Box>
  );
};

export default OvertimeChart;
